const GA4_MEASUREMENT_ID =
  process.env.REACT_APP_GA4_MEASUREMENT_ID ||
  process.env.REACT_APP_GA_MEASUREMENT_ID ||
  'G-LP7ZD7R6WX';

const GTAG_SRC = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;

let isInitialized = false;
let hasClickListener = false;

const canUseBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const ensureDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
};

const loadGtagScript = () => {
  if (!canUseBrowser()) return;

  const existing = document.querySelector(`script[src="${GTAG_SRC}"]`);
  if (existing) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = GTAG_SRC;
  script.setAttribute('data-vruddhi-ga4', 'true');
  document.head.appendChild(script);
};

const DOWNLOAD_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'zip',
  'rar',
  '7z',
  'csv',
  'txt',
  'jpg',
  'jpeg',
  'png',
  'webp',
  'mp4',
  'mov',
];

const getAnchorFromClick = (event) => {
  const target = event?.target;
  if (!target || typeof target.closest !== 'function') return null;
  return target.closest('a[href]');
};

const isExternalUrl = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin !== window.location.origin;
  } catch (error) {
    return false;
  }
};

const isDownloadUrl = (url, anchor) => {
  if (anchor?.hasAttribute('download')) return true;
  try {
    const parsed = new URL(url, window.location.origin);
    const pathname = parsed.pathname.toLowerCase();
    return DOWNLOAD_EXTENSIONS.some((ext) => pathname.endsWith(`.${ext}`));
  } catch (error) {
    return false;
  }
};

const setupAutoInteractionTracking = () => {
  if (!canUseBrowser() || hasClickListener) return;

  document.addEventListener(
    'click',
    (event) => {
      const anchor = getAnchorFromClick(event);
      if (!anchor) return;

      const href = anchor.getAttribute('href') || '';
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;

      const absoluteUrl = (() => {
        try {
          return new URL(href, window.location.origin).toString();
        } catch (error) {
          return '';
        }
      })();

      if (!absoluteUrl) return;

      if (isDownloadUrl(absoluteUrl, anchor)) {
        trackEvent('file_download', {
          file_name: absoluteUrl.split('/').pop() || '',
          link_url: absoluteUrl,
          link_text: (anchor.textContent || '').trim().slice(0, 120),
        });
        return;
      }

      if (isExternalUrl(absoluteUrl)) {
        trackEvent('click', {
          link_domain: (() => {
            try {
              return new URL(absoluteUrl).hostname;
            } catch (error) {
              return '';
            }
          })(),
          link_url: absoluteUrl,
          outbound: true,
          link_text: (anchor.textContent || '').trim().slice(0, 120),
        });
      }
    },
    true,
  );

  hasClickListener = true;
};

export const initAnalytics = () => {
  if (!canUseBrowser() || !GA4_MEASUREMENT_ID || isInitialized) return;

  loadGtagScript();
  ensureDataLayer();

  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });

  setupAutoInteractionTracking();

  isInitialized = true;
};

export const trackPageView = (pathWithQuery) => {
  if (!canUseBrowser() || !GA4_MEASUREMENT_ID) return;
  if (!isInitialized) initAnalytics();
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', 'page_view', {
    page_path: pathWithQuery || window.location.pathname + window.location.search,
    page_title: document.title,
    page_location: window.location.href,
  });
};

export const trackEvent = (eventName, params = {}) => {
  if (!canUseBrowser() || !GA4_MEASUREMENT_ID || !eventName) return;
  if (!isInitialized) initAnalytics();
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', eventName, params);
};

export const analyticsConfig = {
  measurementId: GA4_MEASUREMENT_ID,
};
